import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { MasterColumnDef, MasterDef } from '@project/contracts'
import { Button, Empty, Icon, Money, Notice, Panel, Table, type ColumnDef } from '../design/index.js'
import { useApi } from '../app/api.js'
import { Link } from '../app/Link.js'
import { navigate, paths, type Query } from '../app/router.js'
import { FormField } from './controls.js'
import { ErrorBlock, Loading } from './feedback.js'
import { ColumnsButton, ExportButton, useChosenColumns } from './columns.js'
import { asText, formatQuantity, formatRate } from './format.js'

const useMasterDef = (route: string) => {
  const api = useApi()
  return useQuery({
    queryKey: ['masterDefs'],
    queryFn: () => api.masterMenu(),
    staleTime: 10 * 60_000,
    select: (all) => all.find((d) => d.route === route),
  })
}

const cell = (col: MasterColumnDef, value: unknown) => {
  if (value === null || value === undefined || value === '') return null
  if (col.type === 'boolean') return value === true ? 'Yes' : 'No'
  if (col.type === 'money') return <Money value={String(value)} />
  if (col.type === 'rate') return <span className="num">{formatRate(String(value))}</span>
  if (col.type === 'qty') return <span className="num">{formatQuantity(String(value))}</span>
  if (col.type === 'code') return <span className="mono">{String(value)}</span>
  return asText(value)
}

/** The list of one master. Columns, search and paging all come from the def. */
export const MasterListPage = ({ companyId, masterRoute, query }: {
  companyId: string; masterRoute: string; query: Query
}) => {
  const api = useApi()
  const defQ = useMasterDef(masterRoute)
  const def = defQ.data
  const [q, setQ] = useState(query['q'] ?? '')
  const [includeRetired, setIncludeRetired] = useState(false)

  const params = useMemo(
    () => ({ pageSize: '50', ...(q ? { q } : {}), ...(includeRetired ? { includeRetired: 'true' } : {}) }),
    [q, includeRetired],
  )
  const chooser = useChosenColumns(def ? `master:${def.key}` : '', def?.columns ?? [])
  const list = useQuery({
    queryKey: ['masterList', api.companyId, def?.key, params],
    queryFn: () => api.masterList(def?.key ?? '', params),
    enabled: Boolean(def?.key),
    placeholderData: (prev) => prev,
  })

  if (defQ.isLoading) return <div className="page"><Loading /></div>
  if (!def) return <div className="page"><ErrorBlock error={new Error(`No master at '${masterRoute}'`)} /></div>

  const columns: ColumnDef<Record<string, unknown>>[] = [
    ...chooser.visible.map((c) => ({
      key: c.key,
      header: c.label,
      width: c.width,
      align: c.align,
      render: (row: Record<string, unknown>) => cell(c, row[c.key]),
    })),
    {
      key: 'active', header: '', width: 120,
      render: (row: Record<string, unknown>) =>
        row['active'] === false ? <span style={{ color: 'var(--muted)' }}>Retired</span> : null,
    },
  ]

  return (
    <div className="page">
      <h1 className="page-title">{def.labelPlural}</h1>
      <p className="page-subtitle">{def.blurb}</p>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '20px 0' }}>
        <Button kind="primary" icon={<Icon name="plus" size={18} />}
          onClick={() => navigate(paths.masterRecord(companyId, masterRoute, 'new'))}>
          Add {def.label.toLowerCase()}
        </Button>
        <input
          className="control" style={{ maxWidth: 320 }} value={q}
          placeholder={`Search ${def.labelPlural.toLowerCase()}`}
          onChange={(e) => setQ(e.currentTarget.value)}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, font: '500 14px/1 var(--font-body)' }}>
          <input type="checkbox" checked={includeRetired} onChange={(e) => setIncludeRetired(e.currentTarget.checked)} />
          Show retired
        </label>
        <ColumnsButton all={def.columns} state={chooser} title={`Columns on ${def.labelPlural.toLowerCase()}`} />
        <ExportButton
          href={api.exportUrl('masters', def.key, params, chooser.chosen)}
          disabled={(list.data?.total ?? 0) === 0}
          count={list.data?.total ?? 0}
          what={def.labelPlural.toLowerCase()}
        />
        <span className="num" style={{ marginLeft: 'auto', color: 'var(--muted)' }}>
          {list.data ? `${list.data.total} record${list.data.total === 1 ? '' : 's'}` : ''}
        </span>
      </div>

      {list.error ? <ErrorBlock error={list.error} /> : null}
      {list.data && list.data.rows.length === 0 ? (
        <Empty
          title={`No ${def.labelPlural.toLowerCase()} yet`}
          body={q ? 'Nothing matches that search. Clear it to see everything.' : def.blurb}
          actions={<Button kind="primary" onClick={() => navigate(paths.masterRecord(companyId, masterRoute, 'new'))}>
            Add {def.label.toLowerCase()}
          </Button>}
        />
      ) : (
        <Table
          columns={columns}
          rows={[...(list.data?.rows ?? [])]}
          getKey={(r) => String(r['id'])}
          onRowClick={(r) => navigate(paths.masterRecord(companyId, masterRoute, String(r['id'])))}
        />
      )}
    </div>
  )
}

/** Add or edit one record. The form is the def's field list, nothing more. */
export const MasterRecordPage = ({ companyId, masterRoute, id }: {
  companyId: string; masterRoute: string; id: string
}) => {
  const api = useApi()
  const client = useQueryClient()
  const defQ = useMasterDef(masterRoute)
  const def = defQ.data
  const isNew = id === 'new'

  const record = useQuery({
    queryKey: ['masterRecord', api.companyId, def?.key, id],
    queryFn: () => api.masterRecord(def?.key ?? '', id),
    enabled: Boolean(def?.key) && !isNew,
  })

  const [draft, setDraft] = useState<Record<string, unknown> | null>(null)
  const values = draft ?? record.data ?? {}
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const save = useMutation({
    mutationFn: (fields: Record<string, unknown>) =>
      isNew ? api.masterCreate(def?.key ?? '', fields) : api.masterUpdate(def?.key ?? '', id, fields),
    onSuccess: (saved) => {
      void client.invalidateQueries({ queryKey: ['masterList'] })
      void client.invalidateQueries({ queryKey: ['lookup'] })
      navigate(paths.masterRecord(companyId, masterRoute, String(saved['id'])))
      setDraft(null); setError(null); setFieldErrors({})
    },
    onError: (err: unknown) => {
      const details = (err as { details?: { fields?: Record<string, string> } })?.details
      setFieldErrors(details?.fields ?? {})
      setError(details?.fields ? null : err instanceof Error ? err.message : 'Could not save.')
    },
  })

  const retire = useMutation({
    mutationFn: (active: boolean) => api.masterSetActive(def?.key ?? '', id, active),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['masterRecord'] }); void client.invalidateQueries({ queryKey: ['masterList'] }) },
  })

  if (defQ.isLoading || (!isNew && record.isLoading)) return <div className="page"><Loading /></div>
  if (!def) return <div className="page"><ErrorBlock error={new Error(`No master at '${masterRoute}'`)} /></div>
  if (record.error) return <div className="page"><ErrorBlock error={record.error} /></div>

  const set = (key: string, value: unknown) => setDraft({ ...values, [key]: value })
  const retired = values['active'] === false

  return (
    <div className="page">
      <Link to={paths.masterList(companyId, masterRoute)} className="crumb">
        <Icon name="left" size={15} /> {def.labelPlural}
      </Link>
      <h1 className="page-title">
        {isNew ? `New ${def.label.toLowerCase()}` : asText(values[def.columns[0]?.key ?? 'name']) || def.label}
      </h1>

      {retired ? (
        <div style={{ margin: '16px 0' }}>
          <Notice kind="warn">
            This {def.label.toLowerCase()} is retired. It stays on documents that already use it, but cannot be chosen on new ones.
          </Notice>
        </div>
      ) : null}
      {error ? <div style={{ margin: '16px 0' }}><Notice kind="bad">{error}</Notice></div> : null}

      <Panel title="Details">
        <div className="field-grid">
          {def.fields.map((field) => (
            <FormField
              key={field.key}
              field={field}
              value={(values[field.key] ?? '') as string}
              onChange={(v) => set(field.key, v)}
              error={fieldErrors[field.key]}
            />
          ))}
        </div>
      </Panel>

      <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
        <Button kind="primary" disabled={save.isPending} onClick={() => save.mutate(values)}>
          {save.isPending ? 'Saving…' : isNew ? `Create ${def.label.toLowerCase()}` : 'Save changes'}
        </Button>
        <Button kind="ghost" onClick={() => navigate(paths.masterList(companyId, masterRoute))}>Cancel</Button>
        {!isNew && def.softDelete ? (
          <Button kind="ghost" disabled={retire.isPending} onClick={() => retire.mutate(retired)}>
            {retired ? 'Restore' : 'Retire'}
          </Button>
        ) : null}
      </div>
    </div>
  )
}

/** The Masters menu, grouped the way the registry groups them. */
export const MastersHub = ({ companyId }: { companyId: string }) => {
  const api = useApi()
  const menu = useQuery({ queryKey: ['masterDefs'], queryFn: () => api.masterMenu(), staleTime: 10 * 60_000 })

  const groups = useMemo(() => {
    const byGroup = new Map<string, MasterDef[]>()
    for (const d of menu.data ?? []) {
      const list = byGroup.get(d.group)
      if (list) list.push(d)
      else byGroup.set(d.group, [d])
    }
    return [...byGroup.entries()]
  }, [menu.data])

  if (menu.isLoading) return <div className="page"><Loading /></div>
  if (menu.error) return <div className="page"><ErrorBlock error={menu.error} /></div>

  return (
    <div className="page">
      <h1 className="page-title">Masters</h1>
      <p className="page-subtitle">The reference data every document is built from.</p>
      {groups.map(([group, items]) => (
        <Panel key={group} title={group} aside={`${items.length} master${items.length === 1 ? '' : 's'}`}>
          <div className="tile-grid">
            {items.map((d) => (
              <div className="tile" key={d.key}>
                <div className="tile__body">
                  <Link to={paths.masterList(companyId, d.route)} className="tile__title">{d.labelPlural}</Link>
                  <p className="tile__desc">{d.blurb}</p>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      ))}
    </div>
  )
}
